import { useMemo, useRef } from 'react'
import type { MutableRefObject, ReactNode } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { RoundedBox } from '@react-three/drei'
import { Merkplaat, Scanner, buisGeometrie } from './onderdelen'
import type { TruckKleuren } from './onderdelen'
import { DrivenOpschrift, PuWiel, Tussen, useMaticMaterialen } from './bouwdelen'
import type { Mat } from './bouwdelen'
import { useAnimatie } from '../../scene/store'
import { R_VORKMATEN } from './tempo'

/**
 * R-MATIC — autonome reachtruck (Linde R16 HD automated, serie 1120).
 *
 * ## Waar de maten vandaan komen
 *
 * Uit het VDI-typeblad en de maattekening, niet uit de foto's: l1 2615,
 * l7 1969, b1 1561, b4 920, h1 3430, h6 2516, h8 307,5, reach 537, vork
 * 45 × 100 × 1150. De negen studiobeelden in `docs/referenties/r-matic/`
 * geven de vorm tussen die maten. Alles staat in `docs/rmatic-maten.md`.
 *
 * De opbouw:
 *
 *  - **chassis met steunpoten** (+z): twee poten die naar de punt uitwaaieren
 *    tot 1561 breed, met lastwielen en een scanner in de punt. De mast rijdt
 *    over de poten 537 mm naar voren — de "reach";
 *  - **mast**: triplex, met twee ruitvormige slangrollen op de zijkant en
 *    bovenop een sensorbeugel met een X-plaat en de 3D-camera;
 *  - **kuip** (−z): rode carrosserie met een punt in de voorrand waar de
 *    accu zichtbaar wordt. De stoel staat dwars, de bestuurder kijkt naar
 *    +x, en de instap zit in de achterwand, aan de +x-kant;
 *  - **beschermdak** met gebogen stijlen en een roosterdak, met achterop de
 *    Balyo-module: lidar, noodstop, Bluespot-projector.
 *
 * ## Oorsprong
 *
 * Het draaipunt, 1,2 m voor de achterkant. In de hal draait hij daar een
 * kwartslag om de stelling in te gaan (`Matic`); vanuit dit punt blijven
 * achterkant, poten en vorkpunten binnen 1,46 m, ruim binnen het halve
 * gangpad van 1,7 m.
 */

/** Afstand in mm vanaf de achterkant → z in meters vanaf het draaipunt. */
const s = (mm: number) => mm / 1000 - 1.2

/** Bovenkant beschermdak (het dak zelf, 2,2 m op de draaitafel); de lidar komt tot h6 = 2516. */
const DAK_H = 2.2
const H6 = 2.516
/**
 * Mast ingetrokken (h1), zijn plek in z (ingetrokken) en halve breedte. De
 * hal gebruikt 3430 uit het typeblad van de beurs; de draaitafel van Linde
 * toont de kortste mast van de PDF, 2980 (zie `docs/linde-draaitafels.md`).
 */
export const R_MAST = { beurs: 3.43, linde: 2.98 }
const MAST_Z0 = s(1051)
const MAST_Z1 = s(1362)
/**
 * Halve mastbreedte: 0,67 m over de buitenmast op het vooraanzicht van de
 * draaitafel (frame 31, gemeten tegen b1 = 1561 over de poten).
 */
const MAST_HB = 0.335
/** Binnenkant van buiten-, midden- en binnenmast. */
const BUITEN_B = 0.265
const MIDDEN_B = 0.19
const BINNEN_B = 0.115
/** Kuip: achterkant, bovenrand, en de onderrand boven het chassis. */
const ACHTER = s(140)
const KUIP_H = 1.17
const KUIP_ONDER = 0.277
/**
 * Achterhoeken van de kuip in bovenaanzicht: rond, straal 150 mm (draaitafel,
 * frame 9 en 13). Het middelpunt ligt zo dat de ronding precies aansluit op
 * de buitenkant van de zijwand (x 0,635) en van de achterwand.
 */
const HOEK = { r: 0.15, x: 0.635 - 0.15, z: ACHTER - 0.03 + 0.15 }
/** Poten: h8, begin van het verhoogde pootdeel, punt. */
const POOT_H = 0.3075
const POOT_Z0 = s(1335)
const POOT_Z1 = s(1969)

/**
 * Vork gezakt: het blad ligt vlak boven de poten. Zonder werkcyclus (de
 * showroom) heft hij tot het eerste stellingniveau: de pallet staat op
 * 1,92 m, de ligger komt tot 1,97; het midden van het blad op 2,00 m. In de
 * hal werkt hij op het eerste en tweede niveau; boven de vrije heffing
 * schuift de mast dan uit.
 */
const VORK_Y = POOT_H + 0.055
const HEF_HOOGTE = 2.0 - VORK_Y
/** Vrije heffing h2: pas daarboven schuiven midden- en binnenmast uit. */
const VRIJE_HEF = 2.301
/** Reach l4. */
const REACH = R_VORKMATEN.reach

/** Wat de werkcyclus (`Matic`) van de vorken moet weten. */
export const R_VORK = { rust: VORK_Y, blad: R_VORKMATEN.blad, reach: REACH }
/** De lastwielas, 1812 mm vanaf achter: daar draait hij om als hij de stelling in draait. */
export const R_DRAAIPUNT = s(1812)

/* ------------------------------------------------------------------ vormen */

/**
 * Zijprofiel van de kuip in (z, y). De voorrand volgt de draaitafel van
 * Linde (zijaanzicht, frame 4, 335 px/m): recht omlaag van de bovenrand tot
 * 0,97 m, dan schuin naar achteren tot 650 mm vanaf achter bij de onderrand.
 * Het grijze driehoekige vlak ervoor is de accukast.
 */
function kuipVorm() {
  // Achter begint de wand waar de ronde hoek ophoudt (`HOEK`); de extrusie
  // legt er nog 30 mm afschuining omheen, die in de hoek verdwijnt.
  const v = new THREE.Shape()
  const z0 = HOEK.z + 0.03
  v.moveTo(z0, KUIP_ONDER)
  v.lineTo(s(650), KUIP_ONDER)
  v.quadraticCurveTo(s(700), 0.5, s(820), 0.8)
  v.quadraticCurveTo(s(890), 0.95, s(900), 1.0)
  v.lineTo(s(950), KUIP_H)
  v.lineTo(z0, KUIP_H)
  v.lineTo(z0, KUIP_ONDER)
  return v
}

/** Een zijwand van de kuip: het profiel, geëxtrudeerd over `dikte` in −x. */
function wandGeometrie(dikte: number) {
  const g = new THREE.ExtrudeGeometry(kuipVorm(), {
    depth: dikte, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.03, bevelSegments: 3, curveSegments: 10,
  })
  // Vorm in (z, y), extrusie langs +z; een kwartslag om y legt de vormas op
  // z en de extrusie op −x.
  g.rotateY(-Math.PI / 2)
  bolNormalen(g)
  return g
}

/**
 * De kuip van Linde bolt: het licht verloopt over de hele zijwand, van de
 * voorrand naar de ronde achterhoek. Een extrusie heeft op zijn vlakke
 * zijden geen punten binnenin om te laten bollen, maar wel normalen: die
 * laten we op de twee zijvlakken naar de randen toe naar buiten hellen, als
 * bij een kussen. De GPU interpoleert ze over de grote driehoeken, dus de
 * wand leest als gewelfd zonder één punt extra.
 */
function bolNormalen(g: THREE.BufferGeometry) {
  g.computeBoundingBox()
  const bb = g.boundingBox!
  const zc = (bb.min.z + bb.max.z) / 2
  const yc = (bb.min.y + bb.max.y) / 2
  const lz = (bb.max.z - bb.min.z) / 2
  const hy = (bb.max.y - bb.min.y) / 2
  const pos = g.attributes.position as THREE.BufferAttribute
  const nor = g.attributes.normal as THREE.BufferAttribute
  const n = new THREE.Vector3()
  for (let i = 0; i < pos.count; i++) {
    const nx = nor.getX(i)
    if (Math.abs(nx) < 0.99) continue
    n.set(nx, ((pos.getY(i) - yc) / hy) * 0.22, ((pos.getZ(i) - zc) / lz) * 0.3).normalize()
    nor.setXYZ(i, n.x, n.y, n.z)
  }
  nor.needsUpdate = true
}

/**
 * Achterwand van de kuip, in (x, y), zoals op het achteraanzicht (frame 13):
 * over de hele breedte rood tot de trede op 0,42 m, en aan de stoelkant (−x)
 * een ronde rand die diagonaal oploopt tot de bovenrand. Daarbinnen is de
 * instap. Dikte 100 mm met een ronde afschuining, geëxtrudeerd naar −z.
 */
function achterwandGeometrie() {
  const v = new THREE.Shape()
  // Tot waar de ronde hoek begint; de afschuining van 30 mm komt erbij.
  const b = HOEK.x - 0.03
  const r = 0.04
  v.moveTo(-b + r, KUIP_ONDER)
  v.lineTo(b - r, KUIP_ONDER)
  v.quadraticCurveTo(b, KUIP_ONDER, b, KUIP_ONDER + r)
  v.lineTo(b, 0.42)
  v.lineTo(0.06, 0.42)
  v.quadraticCurveTo(-0.02, 0.42, -0.08, 0.5)
  v.quadraticCurveTo(-0.28, 0.8, -0.38, 0.98)
  v.quadraticCurveTo(-0.42, KUIP_H, -b + r, KUIP_H)
  v.quadraticCurveTo(-b, KUIP_H, -b, KUIP_H - r)
  v.lineTo(-b, KUIP_ONDER + r)
  v.quadraticCurveTo(-b, KUIP_ONDER, -b + r, KUIP_ONDER)
  const g = new THREE.ExtrudeGeometry(v, {
    depth: 0.04, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.03, bevelSegments: 3, curveSegments: 12,
  })
  // Van z = −0,1 tot 0: de mesh staat op de binnenkant van de wand.
  g.translate(0, 0, -0.07)
  return g
}

/**
 * Een poot in bovenaanzicht (z, x): smal naast de kuip, naar de punt toe
 * uitwaaierend tot de buitenmaat van 1561. Positieve x is naar buiten.
 */
function pootVorm() {
  const v = new THREE.Shape()
  v.moveTo(s(950), 0.46)
  v.lineTo(POOT_Z1 - 0.04, 0.46)
  v.quadraticCurveTo(POOT_Z1, 0.46, POOT_Z1, 0.5)
  v.lineTo(POOT_Z1, 0.74)
  v.quadraticCurveTo(POOT_Z1, 0.78, POOT_Z1 - 0.05, 0.78)
  v.quadraticCurveTo(s(1500), 0.78, s(1380), 0.66)
  v.quadraticCurveTo(s(1300), 0.635, s(1200), 0.635)
  v.lineTo(s(950), 0.635)
  v.closePath()
  return v
}

function pootGeometrie(hoogte: number) {
  const g = new THREE.ExtrudeGeometry(pootVorm(), {
    depth: hoogte, bevelEnabled: true, bevelThickness: 0.015, bevelSize: 0.015, bevelSegments: 2, curveSegments: 10,
  })
  // Vorm in (z, x), extrusie langs +z → omhoog: vormas x → z, vormas y → x,
  // extrusie → y.
  g.applyMatrix4(new THREE.Matrix4().set(
    0, 1, 0, 0,
    0, 0, 1, 0,
    1, 0, 0, 0,
    0, 0, 0, 1,
  ))
  return g
}

/** Ruitvormige slangrol op de zijkant van de mast, met het gat achterin. */
function ruitVorm() {
  const v = new THREE.Shape()
  const h = 0.165
  v.moveTo(0, -h)
  v.quadraticCurveTo(0.03, -h, h, -0.03)
  v.quadraticCurveTo(h + 0.02, 0, h, 0.03)
  v.quadraticCurveTo(0.03, h, 0, h)
  v.quadraticCurveTo(-0.03, h, -h, 0.03)
  v.quadraticCurveTo(-h - 0.02, 0, -h, -0.03)
  v.quadraticCurveTo(-0.03, -h, 0, -h)
  const gat = new THREE.Path()
  // Het gat zit in de achterste punt: de vorm-x wijst na de kwartslag naar −z.
  gat.absarc(0.1, 0, 0.035, 0, Math.PI * 2, true)
  v.holes.push(gat)
  return new THREE.ExtrudeGeometry(v, { depth: 0.03, bevelEnabled: false, curveSegments: 10 })
}

/* ------------------------------------------------------------------ chassis */

function Chassis({ m }: { m: Mat }) {
  const poot = useMemo(() => pootGeometrie(POOT_H - 0.07), [])
  return (
    <group>
      {/* Onderbak onder kuip en accu. Tussen de poten blijft de vloer vrij:
          daar rijdt de mast naartoe. */}
      <RoundedBox args={[1.27, KUIP_ONDER - 0.054, s(994) - s(130)]}
        position={[0, (KUIP_ONDER + 0.054) / 2, (s(994) + s(130)) / 2]}
        radius={0.04} smoothness={3} material={m.chassis} castShadow receiveShadow />
      {/* Achterbumper met de achterscanner, midden achter. */}
      <Tussen a={[-0.195, 0.07, s(0)]} b={[0.195, 0.26, s(140)]} m={m.kunststof} />
      <group position={[0, 0.14, s(0) - 0.005]} rotation={[0, Math.PI, 0]}><Scanner schaal={1.4} /></group>

      {/* Poten: uitwaaierend, met in de punt een scannerhuis en het lastwiel. */}
      {[-1, 1].map((k) => (
        <group key={k} scale={[k, 1, 1]}>
          <mesh geometry={poot} position={[0, 0.055, 0]} material={m.chassis} castShadow receiveShadow />
          {/* Naad waar het lastwielhuis begint, op de buitenkant en bovenop. */}
          <Tussen a={[0.64, 0.08, POOT_Z0]} b={[0.66, POOT_H - 0.01, POOT_Z0 + 0.012]} m={m.zwart} schaduw={false} />
          <Tussen a={[0.52, POOT_H - 0.005, POOT_Z0]} b={[0.66, POOT_H + 0.002, POOT_Z0 + 0.012]} m={m.zwart} schaduw={false} />
          {/* Loopvlak voor de mastrollen, aan de binnenkant van de poot. */}
          <Tussen a={[0.46, POOT_H - 0.01, MAST_Z0 - 0.05]} b={[0.52, POOT_H + 0.012, POOT_Z1 - 0.2]} m={m.mast} />
          <RoundedBox args={[0.3, 0.26, 0.2]} position={[0.62, 0.2, POOT_Z1 - 0.1]} radius={0.03} smoothness={2}
            material={m.kunststof} castShadow />
          <mesh position={[0.6, 0.2, POOT_Z1 + 0.002]} material={m.zwart}>
            <circleGeometry args={[0.05, 18]} />
          </mesh>
          <mesh position={[0.6, 0.2, POOT_Z1 + 0.004]} material={m.lens}>
            <circleGeometry args={[0.026, 16]} />
          </mesh>
          <mesh position={[0.771, 0.23, POOT_Z1 - 0.12]} rotation={[0, Math.PI / 2, 0]} material={m.geel}>
            <planeGeometry args={[0.05, 0.05]} />
          </mesh>
          {/* Gele markering op de achterhoeken; arcering staat op de
              draaitafel nergens. */}
          <mesh position={[0.45, 0.15, s(0) - 0.001]} rotation={[0, Math.PI, 0]} material={m.geel}>
            <planeGeometry args={[0.04, 0.04]} />
          </mesh>
          <mesh position={[0.636, 0.18, s(180)]} rotation={[0, Math.PI / 2, 0]} material={m.geel}>
            <planeGeometry args={[0.06, 0.04]} />
          </mesh>
        </group>
      ))}
      {/* Lastwielen Ø285 × 100 op spoorbreedte 1490; aandrijfwiel Ø360 × 130. */}
      {[-1, 1].map((k) => <PuWiel key={k} p={[k * 0.68, 0.1425, s(1812)]} r={0.1425} b={0.1} m={m} />)}
      <PuWiel p={[0.22, 0.18, s(432)]} r={0.18} b={0.13} m={m} />
      <PuWiel p={[-0.42, 0.07, s(380)]} r={0.07} b={0.06} m={m} />
    </group>
  )
}

/* ------------------------------------------------------------------ kuip */

function Kuip({ m }: { m: Mat }) {
  // Beide zijwanden zijn even hoog: de instap zit in de achterwand, niet
  // opzij (draaitafel, frame 4 en 13).
  const wand = useMemo(() => wandGeometrie(0.1), [])
  const achterwand = useMemo(() => achterwandGeometrie(), [])

  return (
    <group>
      {/* Zijwanden: rood gespoten, allebei tot de bovenrand. */}
      <mesh geometry={wand} position={[-0.505, 0, 0]} material={m.lakRood} castShadow receiveShadow />
      <mesh geometry={wand} position={[0.605, 0, 0]} material={m.lakRood} castShadow receiveShadow />
      {/* Achterwand: één rode vorm met de instap erin (zie achterwandGeometrie),
          en de grijze trede op de lage kant. */}
      <mesh geometry={achterwand} position={[0, 0, ACHTER + 0.1]} material={m.lakRood} castShadow receiveShadow />
      {/* De ronde achterhoeken, van onder tot de bovenrand. */}
      {[-1, 1].map((k) => (
        <mesh key={k} position={[k * HOEK.x, (KUIP_H + KUIP_ONDER) / 2, HOEK.z]} material={m.lakRood} castShadow receiveShadow>
          <cylinderGeometry args={[HOEK.r, HOEK.r, KUIP_H - KUIP_ONDER, 20, 1, false, k > 0 ? Math.PI / 2 : Math.PI, Math.PI / 2]} />
        </mesh>
      ))}
      <Tussen a={[0.0, 0.42, ACHTER - 0.02]} b={[0.53, 0.445, s(400)]} m={m.zwaai} />
      {/* Grijze zijpanelen van de accukast: in de driehoek vóór de schuine
          voorrand van de rode wanden. */}
      {[-1, 1].map((k) => (
        <Tussen key={k} a={[k * 0.52, KUIP_ONDER, s(620)]} b={[k * 0.6, KUIP_H, s(955)]} m={m.zwaai} />
      ))}
      {/* Vloer, en de accu met zijn donkere plaat vóór de kuip. */}
      <Tussen a={[-0.5, KUIP_ONDER, ACHTER + 0.1]} b={[0.5, 0.42, s(950)]} m={m.kunststof} />
      <Tussen a={[-0.6, KUIP_ONDER, s(760)]} b={[0.6, 1.08, s(994)]} m={m.chassis} />
      {/* Grijze rand op de wanden. */}
      <Tussen a={[-0.64, KUIP_H - 0.01, HOEK.z]} b={[-0.5, KUIP_H + 0.04, s(950)]} m={m.zwaai} />
      <Tussen a={[0.5, KUIP_H - 0.01, HOEK.z]} b={[0.64, KUIP_H + 0.04, s(950)]} m={m.zwaai} />
      <Tussen a={[-0.5, KUIP_H - 0.01, s(900)]} b={[0.5, KUIP_H + 0.04, s(990)]} m={m.zwaai} />

      {[-1, 1].map((k) => (
        <group key={k}>
          {/* Woordmerk hoog op de wand, net onder de grijze rand. */}
          <Merkplaat positie={[k * 0.637, 1.06, s(330)]} rotatie={[0, k * Math.PI / 2, 0]} breedte={0.2} type="" beugel={false} />
        </group>
      ))}
      {/* Achterkant: een donkergrijs paneel langs de schuine rand, met het
          merk en het rooster erin (draaitafel, frame 13). */}
      <group position={[-0.27, 0.54, ACHTER - 0.034]} rotation={[0, Math.PI, 0.32]}>
        <RoundedBox args={[0.46, 0.13, 0.012]} radius={0.005} smoothness={2} material={m.zwaai} />
        <Merkplaat positie={[0.07, 0.01, 0.007]} breedte={0.15} type="R-MATIC" beugel={false} />
        {[0, 1, 2, 3, 4].map((i) => (
          <Tussen key={i} a={[-0.2, -0.045 + i * 0.02, 0.006]} b={[-0.06, -0.038 + i * 0.02, 0.009]}
            m={m.zwart} schaduw={false} />
        ))}
      </group>

      {/* Stoel, dwars: de bestuurder kijkt naar +x (SIP 910–1040). */}
      {/* De stoel zoals op de draaitafel: grijze voet, zitting en leuning met
          opstaande zijwangen, zwart stof. */}
      <Tussen a={[-0.42, 0.42, s(470)]} b={[-0.18, 0.78, s(700)]} m={m.zwaai} />
      <RoundedBox args={[0.44, 0.09, 0.44]} position={[-0.28, 0.83, s(585)]} radius={0.04} smoothness={3}
        material={m.zwart} castShadow />
      {[-1, 1].map((k) => (
        <RoundedBox key={k} args={[0.4, 0.06, 0.07]} position={[-0.29, 0.89, s(585) + k * 0.19]} radius={0.028} smoothness={3}
          material={m.zwart} castShadow />
      ))}
      <group position={[-0.49, 1.16, s(585)]} rotation={[0, 0, 0.1]}>
        <RoundedBox args={[0.09, 0.56, 0.4]} radius={0.04} smoothness={3} material={m.zwart} castShadow />
        {[-1, 1].map((k) => (
          <RoundedBox key={k} args={[0.12, 0.5, 0.07]} position={[0.02, -0.02, k * 0.2]} radius={0.03} smoothness={3}
            material={m.zwart} castShadow />
        ))}
        {/* Naden in het stof. */}
        {[-0.07, 0.07].map((z) => (
          <Tussen key={z} a={[0.045, -0.2, z - 0.004]} b={[0.047, 0.22, z + 0.004]} m={m.kunststof} schaduw={false} />
        ))}
      </group>
      {/* Console vóór de bestuurder: stuurknop en scherm. */}
      <Tussen a={[0.12, 0.42, s(700)]} b={[0.5, 1.08, s(930)]} m={m.kunststof} />
      <mesh position={[0.3, 1.1, s(820)]} material={m.zwart}>
        <cylinderGeometry args={[0.08, 0.08, 0.03, 20]} />
      </mesh>
      <mesh position={[0.119, 0.98, s(820)]} rotation={[0, -Math.PI / 2, 0]} material={m.scherm}>
        <planeGeometry args={[0.13, 0.08]} />
      </mesh>
      {/* Armleuning met joystick aan de voorkant van de stoel. */}
      <Tussen a={[-0.24, 0.92, s(700)]} b={[0.0, 1.0, s(780)]} m={m.kunststof} />
      <mesh position={[-0.06, 1.05, s(740)]} material={m.zwart}>
        <cylinderGeometry args={[0.016, 0.02, 0.1, 10]} />
      </mesh>
    </group>
  )
}

/* ------------------------------------------------------------------ beschermdak */

function Beschermdak({ m }: { m: Mat }) {
  // Per kant één lus op de achterhoek: een buitenste stijl die bijna
  // loodrecht opgaat en bovenin naar voren krult, en een binnenste die de
  // lus sluit. Van achteren gezien buigen ze naar buiten. Maten van de
  // draaitafel (zijaanzicht frame 4, achteraanzicht frame 13), gemeten vanaf
  // de achterkant van de kuip — die ligt 140 mm voor de bumper.
  const stijlen = useMemo(() => ({
    buiten: buisGeometrie([[0.58, KUIP_H - 0.02, s(200)], [0.625, 1.55, s(150)], [0.62, 1.95, s(160)],
      [0.6, DAK_H - 0.08, s(230)], [0.58, DAK_H - 0.03, s(320)]], 0.034, 28),
    binnen: buisGeometrie([[0.575, KUIP_H + 0.04, s(330)], [0.6, 1.55, s(325)], [0.6, 1.85, s(310)],
      [0.59, 1.98, s(260)], [0.605, 2.02, s(180)]], 0.022, 24),
    voor: buisGeometrie([[0.54, KUIP_H + 0.03, s(500)], [0.585, 1.55, s(485)], [0.59, 1.9, s(495)],
      [0.57, DAK_H - 0.04, s(540)]], 0.028, 22),
  }), [])
  const dakY = DAK_H - 0.05
  /** Het dak zweeft naar voren vrij: geen stijlen bij de mast. */
  const voor = s(856)
  const achter = ACHTER
  return (
    <group>
      {[-1, 1].map((k) => (
        <group key={k} scale={[k, 1, 1]}>
          <mesh geometry={stijlen.buiten} material={m.mast} castShadow />
          <mesh geometry={stijlen.binnen} material={m.mast} castShadow />
          {/* Noodstop onder aan de achterstijl: geel vlak, rode knop. */}
          <mesh position={[0.64, 1.36, s(200)]} rotation={[0, 0, Math.PI / 2]} material={m.geel}>
            <cylinderGeometry args={[0.035, 0.035, 0.012, 18]} />
          </mesh>
          <mesh position={[0.652, 1.36, s(200)]} rotation={[0, 0, Math.PI / 2]} material={m.noodstop}>
            <cylinderGeometry args={[0.016, 0.018, 0.02, 12]} />
          </mesh>
          {/* Voorstijl halverwege: een gebogen buis van de rand naar het dak,
              iets naar buiten gebold, net achter de schermkast. */}
          <mesh geometry={stijlen.voor} material={m.mast} castShadow />
        </group>
      ))}
      {/* Dak: kader met een omhoog krullende voorrand, en een rooster. */}
      <Tussen a={[-0.62, dakY, achter]} b={[0.62, DAK_H, achter + 0.1]} m={m.mast} />
      <Tussen a={[-0.62, dakY, voor - 0.06]} b={[0.62, DAK_H + 0.03, voor]} m={m.mast} />
      {[-1, 1].map((k) => (
        <Tussen key={k} a={[k * 0.56, dakY, achter]} b={[k * 0.62, DAK_H, voor]} m={m.mast} />
      ))}
      {[-0.4, -0.24, -0.08, 0.08, 0.24, 0.4].map((x) => (
        <Tussen key={x} a={[x - 0.02, dakY + 0.01, achter + 0.1]} b={[x + 0.02, DAK_H - 0.01, voor - 0.06]} m={m.mast} />
      ))}
      {[s(440), s(590), s(740)].map((z) => (
        <Tussen key={z} a={[-0.56, dakY + 0.01, z - 0.015]} b={[0.56, DAK_H - 0.01, z + 0.015]} m={m.mast} />
      ))}

      {/* Scherm onder het dak aan de +x-kant, naar de bestuurder gedraaid. */}
      <group position={[0.53, 1.65, s(580)]} rotation={[0, -Math.PI / 2, 0]}>
        <Tussen a={[-0.12, -0.135, -0.03]} b={[0.12, 0.135, 0]} m={m.kunststof} />
        <mesh position={[0, 0.01, 0.002]}>
          <planeGeometry args={[0.17, 0.12]} />
          <meshStandardMaterial color="#8e97a3" emissive="#2a3340" emissiveIntensity={0.35} roughness={0.2} />
        </mesh>
      </group>

      <BalyoModule m={m} />
    </group>
  )
}

/**
 * De Balyo-module achter op het dak: woordmerk en logo op de achterkant, en
 * bovenop de lidar (scanhoogte 2476, bovenkant h6 = 2516), de noodstop en de
 * Bluespot-projector.
 */
function BalyoModule({ m }: { m: Mat }) {
  const y0 = DAK_H
  const z0 = ACHTER
  const z1 = s(470)
  const zm = (z0 + z1) / 2
  return (
    <group>
      <RoundedBox args={[0.9, 0.2, z1 - z0]} position={[0, y0 + 0.1, zm]} radius={0.04} smoothness={3}
        material={m.mast} castShadow />
      <DrivenOpschrift positie={[0.16, y0 + 0.11, z0 - 0.001]} breedte={0.3} />
      <mesh position={[0, y0 + 0.05, z0 - 0.002]} rotation={[0, Math.PI, Math.PI / 4]} material={m.geel}>
        <planeGeometry args={[0.045, 0.045]} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <Tussen key={i} a={[-0.36, y0 + 0.07 + i * 0.022, z0 - 0.003]} b={[-0.24, y0 + 0.078 + i * 0.022, z0]}
          m={m.zwart} schaduw={false} />
      ))}
      {[-1, 1].map((k) => (
        <mesh key={k} position={[k * 0.44, y0 + 0.03, z0 + 0.02]}>
          <boxGeometry args={[0.03, 0.03, 0.03]} />
          <meshStandardMaterial color="#e8791a" emissive="#e8791a" emissiveIntensity={0.3} />
        </mesh>
      ))}
      {/* Lidar: voet, cilinder met blauwe lenzen. */}
      <Tussen a={[-0.05, y0 + 0.2, zm - 0.06]} b={[0.11, y0 + 0.29, zm + 0.06]} m={m.zwart} />
      <mesh position={[0.03, (y0 + 0.29 + H6) / 2, zm]} material={m.zwart} castShadow>
        <cylinderGeometry args={[0.055, 0.06, H6 - y0 - 0.29, 18]} />
      </mesh>
      {[-1, 1].map((k) => (
        <mesh key={k} position={[0.03 + k * 0.03, 2.476, zm - 0.057]} rotation={[Math.PI / 2, 0, 0]} material={m.lens}>
          <cylinderGeometry args={[0.018, 0.018, 0.01, 12]} />
        </mesh>
      ))}
      {/* Noodstop */}
      <mesh position={[-0.15, y0 + 0.21, zm]} material={m.geel}>
        <cylinderGeometry args={[0.045, 0.045, 0.02, 16]} />
      </mesh>
      <mesh position={[-0.15, y0 + 0.24, zm]} material={m.noodstop}>
        <cylinderGeometry args={[0.035, 0.03, 0.04, 16]} />
      </mesh>
      {/* Bluespot-projector, schuin naar achteren omlaag (hoogte 2361). */}
      <group position={[0.3, 2.34, z0 + 0.02]} rotation={[-0.6, 0, 0]}>
        <RoundedBox args={[0.1, 0.08, 0.14]} radius={0.015} smoothness={2} material={m.zwart} />
        <mesh position={[0, 0, -0.071]} rotation={[0, Math.PI, 0]}>
          <circleGeometry args={[0.025, 14]} />
          <meshStandardMaterial color="#3a8ee0" emissive="#2a6fd0" emissiveIntensity={0.5} />
        </mesh>
      </group>
    </group>
  )
}

/* ------------------------------------------------------------------ mast */

function Mast({ m, midden, binnen, last, lading, MAST_H }: {
  m: Mat
  /** Hoogte van de ingetrokken mast, h1. */
  MAST_H: number
  midden: MutableRefObject<THREE.Group | null>
  binnen: MutableRefObject<THREE.Group | null>
  last: MutableRefObject<THREE.Group | null>
  lading?: ReactNode
}) {
  const ruit = useMemo(() => ruitVorm(), [])
  const zMid = (MAST_Z0 + MAST_Z1) / 2
  return (
    <group>
      {/* Mastvoet tussen de poten (m1 = 75), op rollen over de loopvlakken:
          breder dan de mast, tot op de binnenkant van de poten. */}
      <Tussen a={[-0.46, 0.075, MAST_Z0]} b={[0.46, 0.38, MAST_Z1]} m={m.chassis} />

      {/* Buitenmast: twee profielen met een naad, dwarsbalken onder en boven. */}
      {[-1, 1].map((k) => (
        <group key={k}>
          <Tussen a={[k * BUITEN_B, 0.3, MAST_Z0]} b={[k * MAST_HB, MAST_H, MAST_Z1]} m={m.mast} />
          <Tussen a={[k * (MAST_HB - 0.07), 0.4, MAST_Z1]} b={[k * (MAST_HB - 0.058), MAST_H - 0.05, MAST_Z1 + 0.003]} m={m.chassis} schaduw={false} />
          <Tussen a={[k * (MAST_HB - 0.07), 0.4, MAST_Z0 - 0.003]} b={[k * (MAST_HB - 0.058), MAST_H - 0.05, MAST_Z0]} m={m.chassis} schaduw={false} />
          <mesh position={[k * (BUITEN_B - 0.05), 1.4, MAST_Z0 + 0.04]} material={m.chassis} castShadow>
            <cylinderGeometry args={[0.035, 0.035, 2.1, 12]} />
          </mesh>
          {/* Eén ruitvormige slangrol per kant, met het woordmerk (draaitafel:
              midden op 2,25 m, 0,4 m breed). */}
          <group position={[k * (MAST_HB + (k > 0 ? 0 : 0.03)), 2.25, MAST_Z0 + 0.07]} rotation={[0, Math.PI / 2, 0]} scale={1.2}>
            <mesh geometry={ruit} material={m.mast} castShadow />
            <Merkplaat positie={[-0.02, 0.0, k > 0 ? 0.032 : -0.002]} rotatie={[0, k > 0 ? 0 : Math.PI, 0]} breedte={0.13} type="" beugel={false} />
          </group>
          {/* Gele drukknop (1060) en blauw lampje op de zijkant. */}
          <mesh position={[k * (MAST_HB + 0.006), 1.06, zMid]} rotation={[0, 0, Math.PI / 2]} material={m.geel}>
            <cylinderGeometry args={[0.045, 0.045, 0.012, 18]} />
          </mesh>
          <mesh position={[k * (MAST_HB + 0.014), 1.06, zMid]} rotation={[0, 0, Math.PI / 2]} material={m.noodstop}>
            <cylinderGeometry args={[0.02, 0.02, 0.012, 12]} />
          </mesh>
          <mesh position={[k * (MAST_HB + 0.003), 2.7, zMid]} rotation={[0, k * Math.PI / 2, 0]}>
            <planeGeometry args={[0.07, 0.1]} />
            <meshStandardMaterial color="#e9e6e0" roughness={0.6} />
          </mesh>
        </group>
      ))}
      <Tussen a={[-BUITEN_B, 0.3, MAST_Z0]} b={[BUITEN_B, 0.42, MAST_Z0 + 0.08]} m={m.mast} />
      <Tussen a={[-BUITEN_B, MAST_H - 0.1, MAST_Z0]} b={[BUITEN_B, MAST_H, MAST_Z0 + 0.1]} m={m.mast} />
      {[-0.05, 0.05].map((x) => (
        <Tussen key={x} a={[x - 0.012, 0.5, MAST_Z1 - 0.03]} b={[x + 0.012, MAST_H - 0.12, MAST_Z1 - 0.01]}
          m={m.zwart} schaduw={false} />
      ))}

      {/* Sensorbeugel op de mastkop, naar achteren: kader met X-plaat, de
          3D-camera ernaast, blauwe lampjes op de hoeken. */}
      <group position={[0, MAST_H - 0.3, MAST_Z0 - 0.08]}>
        <Tussen a={[-0.41, -0.2, -0.05]} b={[0.41, -0.16, 0.08]} m={m.mast} />
        <Tussen a={[-0.41, 0.16, -0.05]} b={[0.41, 0.2, 0.08]} m={m.mast} />
        {[-1, 1].map((k) => (
          <group key={k}>
            <Tussen a={[k * 0.37, -0.2, -0.05]} b={[k * 0.41, 0.2, 0.08]} m={m.mast} />
            <mesh rotation={[0, 0, k * 0.72]} material={m.mast} castShadow>
              <boxGeometry args={[0.07, 0.45, 0.03]} />
            </mesh>
            <mesh position={[k * 0.415, 0.02, 0.0]}>
              <boxGeometry args={[0.012, 0.08, 0.03]} />
              <meshStandardMaterial color="#3a8ee0" emissive="#1d5fb0" emissiveIntensity={0.3} />
            </mesh>
          </group>
        ))}
        <RoundedBox args={[0.16, 0.14, 0.14]} position={[0.3, 0.3, 0.02]} radius={0.02} smoothness={2}
          material={m.mast} castShadow />
        <mesh position={[0.3, 0.3, -0.052]} rotation={[Math.PI / 2, 0, 0]} material={m.zwart}>
          <cylinderGeometry args={[0.035, 0.035, 0.01, 16]} />
        </mesh>
      </group>

      {/* Middenmast en binnenmast: pas boven de vrije heffing (h2) uit. */}
      <group ref={midden}>
        {[-1, 1].map((k) => (
          <Tussen key={k} a={[k * MIDDEN_B, 0.35, MAST_Z0 + 0.05]} b={[k * BUITEN_B, MAST_H - 0.06, MAST_Z1 - 0.04]} m={m.binnen} />
        ))}
        <Tussen a={[-BUITEN_B, MAST_H - 0.14, MAST_Z0 + 0.05]} b={[BUITEN_B, MAST_H - 0.06, MAST_Z0 + 0.12]} m={m.binnen} />
      </group>
      <group ref={binnen}>
        {[-1, 1].map((k) => (
          <Tussen key={k} a={[k * BINNEN_B, 0.4, MAST_Z0 + 0.09]} b={[k * MIDDEN_B, MAST_H - 0.1, MAST_Z1 - 0.06]} m={m.chassis} />
        ))}
        <Tussen a={[-MIDDEN_B, MAST_H - 0.18, MAST_Z0 + 0.09]} b={[MIDDEN_B, MAST_H - 0.1, MAST_Z0 + 0.16]} m={m.chassis} />
      </group>

      <group ref={last}>
        <Vorkenwagen m={m} />
        {lading}
      </group>
    </group>
  )
}

/**
 * Vorkenbord (b3 830) met het hoge lastrek — bovenkant 2434 bij een vork op
 * de vloer — en de vorken, 45 × 100 × 1150.
 */
function Vorkenwagen({ m }: { m: Mat }) {
  const z0 = MAST_Z1 + 0.005
  const rekTop = VORK_Y + 2.31
  return (
    <group>
      <Tussen a={[-0.415, VORK_Y - 0.03, z0]} b={[0.415, VORK_Y + 0.5, z0 + 0.08]} m={m.mast} />
      {[VORK_Y + 0.02, VORK_Y + 0.44].map((y) => (
        <Tussen key={y} a={[-0.415, y, z0 + 0.08]} b={[0.415, y + 0.05, z0 + 0.1]} m={m.chassis} />
      ))}
      {[-1, 1].map((k) => (
        <Tussen key={k} a={[k * 0.385, VORK_Y + 0.5, z0 + 0.02]} b={[k * 0.415, rekTop, z0 + 0.06]} m={m.mast} />
      ))}
      <Tussen a={[-0.415, rekTop - 0.04, z0 + 0.02]} b={[0.415, rekTop, z0 + 0.06]} m={m.mast} />
      <Tussen a={[-0.415, VORK_Y + 1.3, z0 + 0.02]} b={[0.415, VORK_Y + 1.34, z0 + 0.06]} m={m.mast} />
      {[-0.26, -0.09, 0.09, 0.26].map((x) => (
        <Tussen key={x} a={[x - 0.012, VORK_Y + 0.5, z0 + 0.03]} b={[x + 0.012, rekTop - 0.04, z0 + 0.05]} m={m.mast} />
      ))}
      {/* Blauw lampje (1393) en de 3D-camera onder in het rek. */}
      <mesh position={[0.418, VORK_Y + 1.27, z0 + 0.04]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[0.02, 0.05]} />
        <meshStandardMaterial color="#3a8ee0" emissive="#1d5fb0" emissiveIntensity={0.3} />
      </mesh>
      <RoundedBox args={[0.12, 0.07, 0.06]} position={[0.063, VORK_Y + 0.19, z0 + 0.11]} radius={0.012} smoothness={2}
        material={m.zwart} />
      {[-0.25, 0.25].map((x) => (
        <group key={x}>
          <Tussen a={[x - 0.05, VORK_Y - 0.0225, z0 + 0.08]} b={[x + 0.05, VORK_Y + 0.5, z0 + 0.125]} m={m.vork} />
          <Tussen a={[x - 0.05, VORK_Y - 0.0225, z0 + 0.08]} b={[x + 0.05, VORK_Y + 0.0225, z0 + 1.19]} m={m.vork} />
          <Tussen a={[x - 0.05, VORK_Y - 0.0225, z0 + 1.19]} b={[x + 0.05, VORK_Y, z0 + 1.23]} m={m.vork} />
        </group>
      ))}
    </group>
  )
}

/* ------------------------------------------------------------------ de machine */

export function RMatic({ kleuren, hef, stand, lading, mast = R_MAST.beurs }: {
  kleuren: TruckKleuren
  /** h1, de ingetrokken mast; standaard die van de beurs. */
  mast?: number
  hef: MutableRefObject<number>
  /**
   * Vorkstand van de werkcyclus: hoogte van het blad boven de vloer en hoe
   * ver de mast naar voren is (0..1). Gaat vóór `hef`.
   */
  stand?: MutableRefObject<{ hoogte: number; uit: number }>
  /** Wat er op de vorken ligt, in het assenstelsel van de ingetrokken, gezakte vorkenwagen. */
  lading?: ReactNode
}) {
  const m = useMaticMaterialen(kleuren)
  const anim = useAnimatie()
  const schuif = useRef<THREE.Group>(null)
  const midden = useRef<THREE.Group | null>(null)
  const binnen = useRef<THREE.Group | null>(null)
  const last = useRef<THREE.Group | null>(null)

  useFrame(() => {
    const h = anim.aan && anim.mast ? hef.current : 0
    // Eerst heffen, dan de mast naar voren; bij het zakken omgekeerd. Draaien
    // en naar de stelling rijden doet `Matic`, vóór en na deze curve.
    const st = stand?.current
    const hoog = st ? Math.max(0, st.hoogte - VORK_Y) : THREE.MathUtils.smoothstep(h, 0, 0.7) * HEF_HOOGTE
    const uit = (st ? st.uit : THREE.MathUtils.smoothstep(h, 0.7, 1)) * REACH
    const e = Math.max(0, hoog + VORK_Y - VRIJE_HEF)
    if (schuif.current) schuif.current.position.z = uit
    if (last.current) last.current.position.y = hoog
    if (midden.current) midden.current.position.y = e / 2
    if (binnen.current) binnen.current.position.y = e
  })

  return (
    <group>
      <Chassis m={m} />
      <Kuip m={m} />
      <Beschermdak m={m} />
      <group ref={schuif}>
        <Mast m={m} midden={midden} binnen={binnen} last={last} lading={lading} MAST_H={mast} />
      </group>
    </group>
  )
}
